import { useTranslation } from 'react-i18next';
import { PlusCircle, X } from 'lucide-react';
import type { Control, FieldArrayWithId } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';

interface ContactPersonsListProps {
  // eslint_disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  // eslint_disable-next-line @typescript-eslint/no-explicit-any
  fields: FieldArrayWithId<any>[];
  onAppend: () => void;
  onRemove: (index: number) => void;
}

export function ContactPersonsList({
  control,
  fields,
  onAppend,
  onRemove,
}: ContactPersonsListProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-2.5">
      <h1 className="text-xl font-semibold">{t('contactPersons')}</h1>
      {fields.map((item, index) => (
        <div key={item.id} className="border p-3 rounded-md mb-3">
          <div className="flex justify-between items-center mb-2">
            <span>
              {t('contactPerson')} #{index + 1}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onRemove(index)}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex gap-4">
            <FormField
              control={control}
              name={`contactPersons.${index}.name`}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel>
                    {t('name')} <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name={`contactPersons.${index}.role`}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel>{t('role')}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <div className="flex gap-4 mt-2">
            <FormField
              control={control}
              name={`contactPersons.${index}.phone`}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel>
                    {t('phoneNumber')} <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="+998 XX XXX XX XX" />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name={`contactPersons.${index}.email`}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel>{t('emailAddress')}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>
      ))}
      <Button
        type="button"
        variant="link"
        className="self-start"
        onClick={onAppend}
      >
        <PlusCircle className="mr-2" /> {t('addPerson')}
      </Button>
    </div>
  );
}
